import { Warning20Filled } from '@fluentui/react-icons';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectTeamsAssignmentDialog } from '../../..';
import { Button } from '../../../../Button';
import { ConfirmationDialogProvider } from '../../../../ConfirmationDialog/ConfirmationDialog';
import { useConfirmationDialog } from '../../../../hooks/useConfirmationDialog';
import { ResponsiveDialog } from '../../../../ResponsiveDialog/ResponsiveDialog';
import { Tooltip } from '../../../../Tooltip';
import { HomeworkSelection } from './HomeworkSelection';
import { HomeworkView } from './HomeworkView';
import { HomeworkWithAssociatedAssignmentView } from './HomeworkWithAssociatedAssignmentView';
import { BaseTeamsAssignmentData, HomeworkType } from './NewHomework.interfaces';
import {
  FileWithAssignmentResourceItemProps,
  SelectTeamsAssignmentWithResources,
} from './NewHomeworkDialog.interfaces';
import './NewHomeworkDialog.scss';
import { determineHomeworkType } from './NewHomeworkDialog.utils';
import { TeamsHomeworkView } from './TeamsHomeworkView';

export interface NewHomeworkDialogProps {
  open: boolean;
  onCloseClick: () => void;
  onSaveClick: (
    createAsTeamAssignment: boolean,
    selectedAssignment: SelectTeamsAssignmentWithResources,
  ) => void;
  assignments?: SelectTeamsAssignmentWithResources[];
  filePickerResources?: FileWithAssignmentResourceItemProps[];
  onFilePickerOpenClick: (open: boolean) => void;
  onSetFilePickerResources: (filePickerResources: FileWithAssignmentResourceItemProps[]) => void;
  canCreateTeamsAssignment: boolean;
  editModel?: SelectTeamsAssignmentWithResources;
  selectedDate: Date;
}

const createEmptyModel = (selectedDate: Date): SelectTeamsAssignmentWithResources => ({
  resources: [],
  courseName: '',
  assignmentName: '',
  assignmentId: '',
  assignmentDueDate: moment(selectedDate).toISOString(),
  assignmentDescription: '',
  assignmentMinutes: undefined,
  assignmentPoints: undefined,
  id: null,
});

export const NewHomeworkDialog: React.FC<NewHomeworkDialogProps> = ({
  open,
  onCloseClick,
  onSaveClick,
  assignments = [],
  filePickerResources,
  onFilePickerOpenClick,
  onSetFilePickerResources,
  canCreateTeamsAssignment,
  editModel,
  selectedDate,
}) => {
  const { t } = useTranslation();
  const { getConfirmation } = useConfirmationDialog();

  const [isSelectTeamsAssignmentDialogOpen, setIsSelectTeamsAssignmentDialogOpen] = useState(false);
  const [selectedTeamsAssignmentData, setSelectedTeamsAssignmentData] =
    useState<BaseTeamsAssignmentData | null>(null);
  const [selectedHomework, setSelectedHomework] = useState<SelectTeamsAssignmentWithResources>(
    editModel ?? createEmptyModel(selectedDate),
  );
  const [homeworkType, setHomeworkType] = useState(determineHomeworkType(editModel));

  useEffect(() => {
    setSelectedHomework(editModel ?? createEmptyModel(selectedDate));
    setHomeworkType(determineHomeworkType(editModel));
  }, [editModel]);

  const changeDetect = async (
    teamAssignment: SelectTeamsAssignmentWithResources,
    assignmentId: string,
  ) => {
    const confirmation = await getConfirmation({
      content: t('classbook:newassignment:dialog_teams_confirmation', {
        defaultValue: 'Möchten Sie Daten aus der ausgewählten Teamsaufgabe übernehmen?',
      }),
      headerText: '',
      confirmButtonText: t('assume', { defaultValue: 'Übernehmen' }),
      cancelButtonText: t('classbook:newassignment:dialog_teams_confirmation_cancel', {
        defaultValue: 'Verwerfen',
      }),
      showCloseButton: true,
    });

    if (confirmation) {
      setSelectedHomework((prev) => ({
        ...prev,
        assignmentId: teamAssignment.assignmentId,
        assignmentName: teamAssignment.assignmentName,
        assignmentDescription: teamAssignment.assignmentDescription,
      }));
    } else {
      setSelectedHomework((prev) => ({
        ...prev,
        assignmentId: assignmentId,
      }));
      setSelectedTeamsAssignmentData({
        id: teamAssignment.assignmentId,
        name: teamAssignment.assignmentName,
      });
    }
  };

  const teamsAssignmentDataChanged = (teamsAssignmentData: BaseTeamsAssignmentData | null) => {
    setSelectedTeamsAssignmentData(teamsAssignmentData);
    if (!teamsAssignmentData) {
      setSelectedHomework((prev) => ({
        ...prev,
        assignmentName: '',
        assignmentId: '',
      }));
    } else {
      const existingTeamsAssignment = assignments.find(
        (assignment) => assignment.assignmentId === teamsAssignmentData.id,
      );
      if (
        existingTeamsAssignment &&
        selectedHomework.assignmentName &&
        selectedHomework.assignmentDescription
      ) {
        changeDetect(existingTeamsAssignment, teamsAssignmentData.id);
      } else {
        setSelectedHomework((prev) => ({
          ...prev,
          assignmentDescription:
            existingTeamsAssignment?.assignmentDescription ?? prev.assignmentDescription,
          assignmentName: existingTeamsAssignment?.assignmentName ?? prev.assignmentName,
          assignmentId: existingTeamsAssignment?.id ?? '',
          assignmentPoints: existingTeamsAssignment?.id
            ? existingTeamsAssignment.assignmentPoints
            : '',
        }));
      }
    }
  };

  const clearUI = () => {
    teamsAssignmentDataChanged(null);
    setSelectedHomework(createEmptyModel(selectedDate));
  };

  const handleCreateAsTeamsAssignment = (asTeamAssignment: boolean) => {
    setHomeworkType(asTeamAssignment ? HomeworkType.AsTeamsAssignment : HomeworkType.Common);
    if (!asTeamAssignment) {
      teamsAssignmentDataChanged({
        name: selectedHomework.assignmentName,
        id: '',
      });
    }
  };

  const handleAssignmentTypeSelected = (withTeamAssignment: boolean) => {
    if (withTeamAssignment) {
      setIsSelectTeamsAssignmentDialogOpen(true);
    } else {
      setHomeworkType(HomeworkType.Common);
    }
  };

  const handleTeamsAssignmentSelected = (assignmentId: string, assignmentName: string) => {
    teamsAssignmentDataChanged({ id: assignmentId, name: assignmentName });
    setIsSelectTeamsAssignmentDialogOpen(false);
    setHomeworkType(HomeworkType.WithTeamsAssignment);
  };

  const handleCancel = () => {
    teamsAssignmentDataChanged(null);
    setHomeworkType(HomeworkType.NotSelected);
    clearUI();
    onSetFilePickerResources([]);
    onCloseClick();
  };

  const handleRemoveTeamsAssignment = () => {
    teamsAssignmentDataChanged(null);
    setHomeworkType(HomeworkType.Common);
  };

  const handleSelectedHomeworkChanged = (homework: SelectTeamsAssignmentWithResources) =>
    setSelectedHomework(homework);

  const renderHomeworkView = () => {
    switch (homeworkType) {
      case HomeworkType.AsTeamsAssignment:
        return (
          <TeamsHomeworkView
            createAsTeamsAssignmentUnchecked={() => handleCreateAsTeamsAssignment(false)}
            selectedHomework={selectedHomework}
            selectedHomeworkChanged={handleSelectedHomeworkChanged}
            editable={!!selectedTeamsAssignmentData}
            onSetFilePickerResources={onSetFilePickerResources}
            filePickerResources={filePickerResources}
            openFilePickerClicked={() => onFilePickerOpenClick(true)}
            selectedDate={selectedDate}
          />
        );
      case HomeworkType.Common:
        return (
          <HomeworkView
            canCreateTeamsAssignment={canCreateTeamsAssignment}
            createAsTeamsAssignmentChecked={() => handleCreateAsTeamsAssignment(true)}
            selectAssignmentButtonClicked={() => setIsSelectTeamsAssignmentDialogOpen(true)}
            selectedHomework={selectedHomework}
            selectedHomeworkChanged={handleSelectedHomeworkChanged}
            selectedDate={selectedDate}
          />
        );
      case HomeworkType.WithTeamsAssignment:
        return (
          selectedTeamsAssignmentData && (
            <HomeworkWithAssociatedAssignmentView
              assignmentData={selectedTeamsAssignmentData}
              removeTeamsAssignmentClicked={handleRemoveTeamsAssignment}
              selectedHomeworkChanged={handleSelectedHomeworkChanged}
              selectedHomework={selectedHomework}
              selectedDate={selectedDate}
            />
          )
        );
      default:
        return (
          <HomeworkSelection
            canCreateTeamsAssignment={canCreateTeamsAssignment}
            selected={handleAssignmentTypeSelected}
          />
        );
    }
  };

  return (
    <ConfirmationDialogProvider>
      <SelectTeamsAssignmentDialog
        open={isSelectTeamsAssignmentDialogOpen}
        assignments={assignments}
        onAssignmentSelected={handleTeamsAssignmentSelected}
        onCancelClick={() => setIsSelectTeamsAssignmentDialogOpen(false)}
      />
      <ResponsiveDialog
        open={open}
        className="newHomeworkDialog"
        header={
          editModel
            ? t('classbook:newassignment:dialog_header_edit', {
                defaultValue: 'Aufgabe bearbeiten',
              })
            : t('classbook:newassignment:dialog_header', { defaultValue: 'Neue Aufgabe' })
        }
        primaryAction={
          <Button
            appearance="primary"
            disabled={!selectedHomework.assignmentName}
            onClick={() => {
              onSaveClick(
                canCreateTeamsAssignment ? homeworkType === HomeworkType.AsTeamsAssignment : false,
                selectedHomework,
              );
              clearUI();
            }}
          >
            {t('save', { defaultValue: 'Speichern' })}
          </Button>
        }
        secondaryAction={
          <Button onClick={handleCancel}>{t('cancel', { defaultValue: 'Abbrechen' })}</Button>
        }
        headerAction={
          canCreateTeamsAssignment ? undefined : (
            <Tooltip
              className="noOwnerTooltip"
              trigger={<Warning20Filled className="noOwnerIconColor" />}
              content={t('classbook:newassignment:noteamowner_alert', {
                defaultValue:
                  'Sie müssen Besitzer des Teams sein, um Aufgaben mit Teams erstellen zu können.',
              })}
              relationship="label"
            />
          )
        }
      >
        {renderHomeworkView()}
      </ResponsiveDialog>
    </ConfirmationDialogProvider>
  );
};
